import { useEffect, useState, useRef, useCallback } from 'react';
import { indodaxService } from '@/services/indodax.service';
import { logger } from '@/lib/logger';
import { INDODAX_TOKEN, INDODAX_WS_URL } from '@/config/tokens';

interface ChartTickData {
  channel: string;
  data: {
    data: [
      number, // epoch timestamp in second
      number, // sequence number
      number, // price
      string, // volume
    ][];
    offset: number;
  };
}

interface IndodaxWebSocketData {
  priceIdr: number;
  volume: string;
  timestamp: number;
  isConnected: boolean;
  isLoadingInitial: boolean;
  error: string | null;
}

export const useIndodaxWebSocket = (pair: string = 'asetquidr') => {
  const [data, setData] = useState<IndodaxWebSocketData>({
    priceIdr: 0,
    volume: '0',
    timestamp: 0,
    isConnected: false,
    isLoadingInitial: true,
    error: null,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isAuthenticatedRef = useRef(false);
  const requestIdRef = useRef(1);
  const hasInitialDataRef = useRef(false);

  // Fetch initial price from HTTP API
  const fetchInitialPrice = useCallback(async () => {
    if (hasInitialDataRef.current) return;

    try {
      logger.log('[Indodax] Fetching initial price from HTTP API...');
      const priceData = await indodaxService.getAsetquIdrPrice();

      hasInitialDataRef.current = true;
      setData(prev => ({
        ...prev,
        priceIdr: priceData.priceIdr,
        timestamp: priceData.timestamp,
        volume: '0', // HTTP API doesn't provide volume
        isLoadingInitial: false,
        error: null,
      }));

      logger.log('[Indodax] Initial price loaded:', priceData.priceIdr, 'IDR');
    } catch (err) {
      logger.error('[Indodax] Failed to fetch initial price:', err);
      setData(prev => ({
        ...prev,
        isLoadingInitial: false,
        error: 'Failed to fetch initial price',
      }));
    }
  }, []);

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    isAuthenticatedRef.current = false;
  }, []);

  const connect = useCallback(() => {
    try {
      cleanup();

      const ws = new WebSocket(INDODAX_WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        logger.log('[Indodax WS] Connected');

        // Step 1: Authenticate
        const authMessage = {
          params: {
            token: INDODAX_TOKEN,
          },
          id: requestIdRef.current++,
        };

        ws.send(JSON.stringify(authMessage));
      };

      ws.onmessage = event => {
        try {
          const message = JSON.parse(event.data);

          // Handle authentication response
          if (
            message.id &&
            message.result &&
            message.result.client &&
            !isAuthenticatedRef.current
          ) {
            logger.log('[Indodax WS] Authenticated:', message.result.client);
            isAuthenticatedRef.current = true;

            setData(prev => ({ ...prev, isConnected: true, error: null }));

            // Step 2: Subscribe to chart tick channel
            const subscribeMessage = {
              method: 1, // Subscribe method
              params: {
                channel: `chart:tick-${pair}`,
              },
              id: requestIdRef.current++,
            };

            ws.send(JSON.stringify(subscribeMessage));
            logger.log(`[Indodax WS] Subscribing to chart:tick-${pair}`);

            // Step 3: Start ping/pong heartbeat (every 25 seconds)
            if (pingIntervalRef.current) {
              clearInterval(pingIntervalRef.current);
            }

            pingIntervalRef.current = setInterval(() => {
              if (ws.readyState === WebSocket.OPEN) {
                const pingMessage = {
                  method: 7, // Ping method
                  id: requestIdRef.current++,
                };
                ws.send(JSON.stringify(pingMessage));
                logger.log('[Indodax WS] Ping sent');
              }
            }, 25000); // Send ping every 25 seconds
          }

          // Handle subscription confirmation
          if (message.id && message.result && message.result.recoverable !== undefined) {
            logger.log('[Indodax WS] Subscription confirmed, offset:', message.result.offset);
          }

          // Handle ping/pong response
          if (message.id && !message.result && !message.error) {
            logger.log('[Indodax WS] Pong received');
          }

          // Handle chart tick data
          if (
            message.result &&
            message.result.channel &&
            message.result.channel.startsWith('chart:tick-')
          ) {
            const tickData = message.result as ChartTickData;

            if (tickData.data.data && tickData.data.data.length > 0) {
              const latestTick = tickData.data.data[tickData.data.data.length - 1];
              const [timestamp, , price, volume] = latestTick;

              setData(prev => ({
                ...prev,
                priceIdr: price,
                volume,
                timestamp,
                isConnected: true,
                error: null,
              }));

              logger.log(`[Indodax WS] Price update: ${price} IDR, Volume: ${volume}`);
            }
          }
        } catch (err) {
          logger.error('[Indodax WS] Error parsing message:', err);
        }
      };

      ws.onerror = error => {
        logger.error('[Indodax WS] Error:', error);
        setData(prev => ({
          ...prev,
          isConnected: false,
          error: 'WebSocket connection error',
        }));
      };

      ws.onclose = event => {
        logger.log('[Indodax WS] Disconnected:', event.code, event.reason);
        isAuthenticatedRef.current = false;

        setData(prev => ({
          ...prev,
          isConnected: false,
        }));

        // Attempt to reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          logger.log('[Indodax WS] Attempting to reconnect...');
          connect();
        }, 5000);
      };
    } catch (err) {
      logger.error('[Indodax WS] Connection error:', err);
      setData(prev => ({
        ...prev,
        isConnected: false,
        error: 'Failed to establish connection',
      }));
    }
  }, [pair, cleanup]);

  useEffect(() => {
    // Step 1: Fetch initial price from HTTP API
    fetchInitialPrice();

    // Step 2: Connect to WebSocket for real-time updates
    connect();

    return () => {
      cleanup();
    };
  }, [connect, cleanup, fetchInitialPrice]);

  return data;
};
